'use client'

import { IEnvService } from '@/core/services/env/env-service'
import { type Auth, type RecaptchaVerifier } from 'firebase/auth'
import { IAuthServer } from './adapters/auth-server'
import { createFirebaseConfig } from './config'

declare global {
  interface Window {
    recaptchaCloseListener?: boolean
  }
}

class AuthServer implements IAuthServer {
  private auth: Auth | null = null
  private recaptchaVerifier: RecaptchaVerifier | null = null
  private recaptchaObserver?: MutationObserver
  private recaptchaWindowObserver?: MutationObserver

  constructor(private envService: IEnvService) {
    this.initializeAuth(envService)
  }

  private async initializeAuth(envService: IEnvService): Promise<Auth> {
    if (this.auth) return this.auth

    const firebaseConfig = createFirebaseConfig(envService)
    const [{ initializeApp }, { getAuth }] = await Promise.all([
      import('firebase/app'),
      import('firebase/auth'),
    ])

    initializeApp(firebaseConfig)
    const auth = getAuth()
    auth.useDeviceLanguage()
    this.auth = auth

    return auth
  }

  private setupCaptchaCloseListener(onClose: () => void) {
    if (window.recaptchaCloseListener) return
    window.recaptchaCloseListener = true

    if (this.recaptchaObserver) {
      this.recaptchaObserver.disconnect()
    }

    this.recaptchaObserver = new MutationObserver(() => {
      const recaptchaFrame = Array.from(
        document.getElementsByTagName('iframe'),
      ).find((x: HTMLIFrameElement) =>
        x.src.includes('google.com/recaptcha/api2/bframe'),
      )

      if (!recaptchaFrame?.parentElement?.parentElement) return

      const recaptchaWindow = recaptchaFrame.parentElement
        .parentElement as HTMLElement

      if (this.recaptchaWindowObserver) return

      this.recaptchaWindowObserver = new MutationObserver(() => {
        if (
          recaptchaWindow.style.opacity === '0' ||
          recaptchaWindow.style.visibility === 'hidden' ||
          recaptchaWindow.style.display === 'none'
        ) {
          onClose()
        }
      })

      this.recaptchaWindowObserver.observe(recaptchaWindow, {
        attributes: true,
        attributeFilter: ['style'],
      })
    })

    this.recaptchaObserver.observe(document.body, {
      subtree: true,
      childList: true,
    })
  }

  private async initializeRecaptcha(
    captchaPlaceholder: string = 'recaptcha-placeholder',
    onClose?: () => void,
  ) {
    if (!this.recaptchaVerifier) {
      const { RecaptchaVerifier } = await import('firebase/auth')
      let auth = this.auth
      if (!auth) {
        auth = await this.initializeAuth(this.envService)
      }

      this.recaptchaVerifier = new RecaptchaVerifier(auth, captchaPlaceholder, {
        size: 'invisible',
      })

      if (onClose) {
        this.setupCaptchaCloseListener(onClose)
      }
    }
  }

  async loginWithPhoneNumber(
    phoneNumber: string,
    options?: { onCaptchaClosed?: () => void },
  ): Promise<string> {
    await this.initializeRecaptcha(
      'recaptcha-placeholder',
      options?.onCaptchaClosed,
    )

    if (!this.recaptchaVerifier) {
      options?.onCaptchaClosed?.()
      throw new Error('Recaptcha verifier not initialized')
    }

    let auth = this.auth
    if (!auth) {
      auth = await this.initializeAuth(this.envService)
    }

    const { signInWithPhoneNumber } = await import('firebase/auth')

    const result = await signInWithPhoneNumber(
      auth,
      phoneNumber,
      this.recaptchaVerifier,
    )
    return result.verificationId
  }
}

export const createAuthServer = ({
  envService,
}: {
  envService: IEnvService
}): IAuthServer => {
  return new AuthServer(envService)
}
